// src/components/dashboard/Dashboard.jsx - Dashboard with Real Data from Backend
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, TrendingUp, DollarSign, Calendar, Users, BarChart3 } from 'lucide-react';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

// shadcn components
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '../ui/empty';
import { Skeleton } from '../ui/skeleton';

// Layout
import PageContainer from '../ui/layout/PageContainer/PageContainer';
import { cn } from '../../utils/cn';

// Hooks
import { useDashboard } from '../../hooks/useDashboard';

// ================================
// COMPONENT
// ================================

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Get dashboard data from hook
  const {
    dashboardStats,
    dashboardLoading,
    loadDashboardStats
  } = useDashboard();

  // Load dashboard stats on mount
  useEffect(() => {
    loadDashboardStats();
  }, []);

  // Filter table data based on search
  const filteredTableData = searchQuery && dashboardStats?.recent_activity
    ? dashboardStats.recent_activity.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.detail.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : dashboardStats?.recent_activity || [];

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  // Get badge variant for type
  const getTypeBadge = (type) => {
    const badges = {
      customer: { label: 'Customer', variant: 'default' },
      invoice: { label: 'Invoice', variant: 'secondary' },
      appointment: { label: 'Appointment', variant: 'outline' },
    };
    return badges[type] || badges.customer;
  };

  // Get status badge styling
  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-700 border-green-200',
      'Paid': 'bg-green-100 text-green-700 border-green-200',
      'Confirmed': 'bg-blue-100 text-blue-700 border-blue-200',
      'Pending': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'Overdue': 'bg-red-100 text-red-700 border-red-200',
      'Draft': 'bg-gray-100 text-gray-700 border-gray-200',
      'Sent': 'bg-blue-100 text-blue-700 border-blue-200',
      'Cancelled': 'bg-gray-100 text-gray-700 border-gray-200',
    };
    return colors[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Handle row click
  const handleRowClick = (item) => {
    const routes = {
      customer: `/customers/${item.id}`,
      invoice: `/invoices/${item.id}`,
      appointment: `/appointments/${item.id}`,
    };
    navigate(routes[item.type]);
  };

  // Show empty state if no data after loading
  if (!dashboardLoading && !dashboardStats) {
    return (
      <PageContainer padding="sm" maxWidth="7xl">
        <Empty className="min-h-[600px]">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BarChart3 className="h-10 w-10 text-muted-foreground" />
            </EmptyMedia>
            <EmptyTitle>No Dashboard Data</EmptyTitle>
            <EmptyDescription>
              Unable to load dashboard statistics. Please try refreshing the page.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <button
              onClick={() => loadDashboardStats()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            >
              Retry
            </button>
          </EmptyContent>
        </Empty>
      </PageContainer>
    );
  }

  return (
    <PageContainer padding="sm" maxWidth="7xl">
      <div className="space-y-10">

        {/* Revenue Overview Section */}
        <div className="space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Revenue Overview</h2>
            {dashboardLoading ? (
              <div className="space-y-2 mt-2">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            ) : (
              <div className="flex items-baseline gap-2 mt-2">
                <p className="text-3xl font-bold text-foreground">
                  {formatCurrency(dashboardStats?.revenue?.total || 0)}
                </p>
                <p className="text-sm text-muted-foreground">
                  <span className={cn(
                    (dashboardStats?.revenue?.change_percent || 0) > 0 ? "text-chart-1" : "text-chart-5"
                  )}>
                    {(dashboardStats?.revenue?.change_percent || 0) > 0 ? '+' : ''}
                    {dashboardStats?.revenue?.change_percent || 0}%
                  </span>
                  {' '}from last period
                </p>
              </div>
            )}
          </div>

          {/* Revenue Chart */}
          <div className="h-[300px] w-full">
            {dashboardLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dashboardStats?.revenue?.data || []} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                    tickFormatter={(value) => `$${value}`}
                    dx={-10}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '6px',
                      fontSize: '12px'
                    }}
                    formatter={(value) => [`$${value}`, 'Revenue']}
                  />
                  <Area
                    type="monotone"
                    dataKey="revenue"
                    stroke="hsl(var(--chart-1))"
                    strokeWidth={2}
                    fill="url(#revenueGradient)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Two-column grid for Appointments and Customer Growth */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Appointments Chart Section */}
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Appointments</h2>
              {dashboardLoading ? (
                <Skeleton className="h-6 w-32 mt-2" />
              ) : (
                <p className="text-sm text-muted-foreground mt-2">
                  {dashboardStats?.appointments?.total || 0} appointments in the last 14 days
                </p>
              )}
            </div>

            {/* Appointments Chart */}
            <div className="h-[250px] w-full">
              {dashboardLoading ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dashboardStats?.appointments?.data || []} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                      dy={10}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                      dx={-10}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}
                      formatter={(value) => [value, 'Appointments']}
                    />
                    <Line
                      type="monotone"
                      dataKey="appointments"
                      stroke="hsl(var(--chart-2))"
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--chart-2))', r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Customer Growth Section */}
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Customer Growth</h2>
              {dashboardLoading ? (
                <Skeleton className="h-6 w-32 mt-2" />
              ) : (
                <p className="text-sm text-muted-foreground mt-2">
                  {dashboardStats?.customers?.total_new || 0} new customers in the last 12 weeks
                </p>
              )}
            </div>

            {/* Customer Growth Chart */}
            <div className="h-[250px] w-full">
              {dashboardLoading ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dashboardStats?.customers?.data || []} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis
                      dataKey="week"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                      dy={10}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
                      dx={-10}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                        fontSize: '12px'
                      }}
                      formatter={(value) => [value, 'New Customers']}
                      labelFormatter={(label) => {
                        const weekData = dashboardStats?.customers?.data?.find(d => d.week === label);
                        return weekData ? `${weekData.week_start} - ${weekData.week_end}` : label;
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="customers"
                      stroke="hsl(var(--chart-3))"
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--chart-3))', r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <Separator />

        {/* Recent Activity Section */}
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
          </div>

          {/* Search Input */}
          <Input
            placeholder="Search activity..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="max-w-sm"
          />

          {/* Activity Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboardLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                    </TableRow>
                  ))
                ) : filteredTableData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      <div className="text-muted-foreground">
                        {searchQuery ? 'No results found' : 'No recent activity'}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTableData.map((item, index) => {
                    const typeBadge = getTypeBadge(item.type);
                    return (
                      <TableRow
                        key={index}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(item)}
                      >
                        <TableCell>
                          <Badge variant={typeBadge.variant}>{typeBadge.label}</Badge>
                        </TableCell>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="text-muted-foreground">{item.date}</TableCell>
                        <TableCell>
                          <Badge className={cn("border", getStatusColor(item.status))}>
                            {item.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.amount)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{item.detail}</TableCell>
                        <TableCell>
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default Dashboard;
